import React, { useEffect, useState } from 'react';
import { getRelatedPosts } from '../../../services/blogService';
import type { HydratedBlogPost } from '../../types/blog';
import { PageContainer } from '../ui/page-container';
import PostCard from './PostCard';
import PostCardSkeleton from './PostCardSkeleton';

interface RelatedPostsProps {
    post: HydratedBlogPost;
}

type FetchState =
    | { status: 'loading' }
    | { status: 'loaded'; posts: HydratedBlogPost[] }
    | { status: 'empty' };

/**
 * RelatedPosts — renders up to 3 posts related to the current post (mobile + desktop
 * show all 3; tablet shows 2 — the 3rd card is hidden only at md), excluding it.
 * Returns null if 0 related posts remain (does not render the section
 * heading on empty). On fetch error, fails silently and returns null after
 * logging to console.
 *
 * Source: getRelatedPosts (item 10) scores all published posts on shared
 * faceted tags (style/region/audience) + category, recency tiebreak. Untagged
 * posts degrade to same-category + recency (the previous behaviour).
 *
 * Width: this component is mounted as a sibling of the article's narrow
 * PageContainer (NOT nested inside it) by BlogPostPage, so it can apply its
 * own PageContainer size="content" (max-w-7xl) and inhabit the same grid
 * width as /blog and /blog/:category.
 */
const RelatedPosts: React.FC<RelatedPostsProps> = ({ post }) => {
    const [state, setState] = useState<FetchState>({ status: 'loading' });

    useEffect(() => {
        let cancelled = false;
        setState({ status: 'loading' });
        getRelatedPosts(post, 3)
            .then((posts) => {
                if (cancelled) return;
                if (posts.length === 0) {
                    setState({ status: 'empty' });
                } else {
                    setState({ status: 'loaded', posts });
                }
            })
            .catch((err) => {
                if (cancelled) return;
                console.warn('[RelatedPosts] Failed to fetch related posts:', err);
                setState({ status: 'empty' });
            });
        return () => {
            cancelled = true;
        };
    }, [post]);

    if (state.status === 'empty') return null;

    return (
        <section className="mt-16">
            <PageContainer size="content">
                <h2 className="text-2xl font-bold text-text-primary mb-6">You might also like</h2>
                <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6" aria-busy={state.status === 'loading'}>
                    {state.status === 'loading'
                        ? (
                            <>
                                <span className="sr-only" role="status" aria-label="Loading related articles" />
                                {Array.from({ length: 3 }).map((_, i) => (
                                    i === 2
                                        ? <div key={i} className="md:hidden lg:block"><PostCardSkeleton /></div>
                                        : <PostCardSkeleton key={i} />
                                ))}
                            </>
                        )
                        : state.posts.map((post, i) => (
                            // 3rd related post: shown on mobile + desktop, hidden on tablet (md)
                            i === 2
                                ? <div key={post.id} className="md:hidden lg:block"><PostCard post={post} /></div>
                                : <PostCard key={post.id} post={post} />
                        ))}
                </div>
            </PageContainer>
        </section>
    );
};

/**
 * Standalone loading skeleton for the related-posts section — the "You might
 * also like" heading + a full row of 3 card skeletons, in the same wide
 * PageContainer + grid as the loaded section. Used by BlogPostPage during the
 * POST fetch (RelatedPosts itself can't mount until the post is known). Marked
 * aria-hidden — the page-level "Loading article" status does the announcing.
 */
export const RelatedPostsSkeleton: React.FC = () => (
    <section className="mt-16" aria-hidden="true">
        <PageContainer size="content">
            <h2 className="text-2xl font-bold text-text-primary mb-6">You might also like</h2>
            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
                {Array.from({ length: 3 }).map((_, i) => (
                    i === 2
                        ? <div key={i} className="md:hidden lg:block"><PostCardSkeleton /></div>
                        : <PostCardSkeleton key={i} />
                ))}
            </div>
        </PageContainer>
    </section>
);

export default RelatedPosts;
